import type { MutationOptions } from '@shared/innmaxLib/services'
import {
  convertData,
  request,
  useMakeMutation,
} from '@shared/innmaxLib/services'
import { prop } from 'ramda'
import React from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import {
  useConvertToChargingFee,
  useConvertToChargingFeeDiscount,
} from './chargingFee.converter'

export const useAddChargingFee = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/chargingFee', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useUpdateChargingFee = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/chargingFee/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useChargingFeePagination = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, ...others } = useQuery(
    ['/chargingFee/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const converter = useConvertToChargingFee()

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter,
      },
      data
    )
  }, [data, converter])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useChargingFee = (query?: any, options?: UseQueryOptions) => {
  const { data: _data, ...others } = useQuery(['/chargingFee/{id}', query], {
    enabled: !!query?.id,
    ...options,
  })

  const converter = useConvertToChargingFee()

  const data = React.useMemo(() => {
    return converter(prop('data', _data as any))
  }, [_data, converter])

  return {
    ...others,
    data,
    loading: others.isLoading,
  }
}

export const useAddChargingFeeDiscount = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/chargingFee/discount', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useUpdateChargingFeeDiscount = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/chargingFee/discount/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useDeleteChargingFeeDiscount = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/chargingFee/discount/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

export const useChargingFeeDiscountPagination = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, ...others } = useQuery(
    ['/chargingFee/discount/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const converter = useConvertToChargingFeeDiscount()

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter,
      },
      data
    )
  }, [data, converter])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useChargingFeeDiscount = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data: _data, ...others } = useQuery(
    ['/chargingFee/discount/{id}', query],
    {
      enabled: !!query?.id,
      ...options,
    }
  )

  const converter = useConvertToChargingFeeDiscount()

  const data = React.useMemo(() => {
    return converter(prop('data', _data as any))
  }, [_data, converter])

  return {
    ...others,
    data,
    loading: others.isLoading,
  }
}
